import type { ReactNode } from 'react';

import type { RouteFeature } from '../types';

export interface RouteAlternativesLabels {
  alternatives: string;
  active: string;
  inactive: string;
  focused: string;
  focus: string;
  activate: string;
  deactivate: string;
}

export function toggleRouteSelection(activeRouteNums: number[], routeNum: number): number[] {
  return activeRouteNums.includes(routeNum)
    ? activeRouteNums.filter((activeRouteNum) => activeRouteNum !== routeNum)
    : [...activeRouteNums, routeNum];
}

export function normalizeSavedRouteSelection(saved: unknown, availableRouteNums: number[]): number[] {
  if (!Array.isArray(saved)) return [];
  const available = new Set(availableRouteNums);
  return [...new Set(saved.map(Number).filter((routeNum) => Number.isFinite(routeNum) && available.has(routeNum)))];
}

export function orderActiveRouteFeatures(
  routes: RouteFeature[],
  activeRouteNums: number[],
  focusedRouteNum: number | null
): RouteFeature[] {
  const active = routes.filter((route) => activeRouteNums.includes(route.properties.route_num));
  if (focusedRouteNum === null) return active;
  return active.sort((left, right) =>
    Number(right.properties.route_num === focusedRouteNum) - Number(left.properties.route_num === focusedRouteNum));
}

export interface RouteAlternativesProps {
  routes: RouteFeature[];
  activeRouteNums: number[];
  focusedRouteNum: number | null;
  labels: RouteAlternativesLabels;
  formatMetrics: (route: RouteFeature) => ReactNode;
  onToggleActive: (routeNum: number) => void;
  onFocus: (routeNum: number) => void;
}

export function RouteAlternatives({
  routes,
  activeRouteNums,
  focusedRouteNum,
  labels,
  formatMetrics,
  onToggleActive,
  onFocus
}: RouteAlternativesProps) {
  return (
    <section className="route-alternatives" aria-label={labels.alternatives}>
      <h3>{labels.alternatives}</h3>
      <div className="route-alternatives__list">
        {routes.map((route) => {
          const routeNum = route.properties.route_num;
          const name = route.properties.short_name || route.properties.name;
          const active = activeRouteNums.includes(routeNum);
          const focused = focusedRouteNum === routeNum;
          return (
            <article
              key={routeNum}
              className="route-alternative"
              data-active={active}
              data-focused={focused}
              aria-label={name}
            >
              <span className="route-alternative__color" style={{ backgroundColor: route.properties.color }} aria-hidden="true" />
              <div className="route-alternative__summary">
                <strong>{name}</strong>
                <small>{formatMetrics(route)}</small>
                <span className="route-alternative__states">
                  <span>{active ? labels.active : labels.inactive}</span>
                  {focused && <span>{labels.focused}</span>}
                </span>
              </div>
              <div className="route-alternative__actions">
                <button
                  type="button"
                  aria-label={`${active ? labels.deactivate : labels.activate} ${name}`}
                  aria-pressed={active}
                  onClick={() => onToggleActive(routeNum)}
                >
                  {active ? labels.deactivate : labels.activate}
                </button>
                <button
                  type="button"
                  aria-label={`${labels.focus} ${name}`}
                  aria-current={focused ? 'true' : undefined}
                  onClick={() => onFocus(routeNum)}
                >
                  {labels.focus}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
